import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import config from "../../../config/config.js";
import { useAuth } from "../../login/AuthContext";
import { toast } from "react-toastify";
import CustomerStep from "../../orders/steps/CustomerStep.jsx";
import "../../customers/customers.css";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

const STATUS_TYPES = {
  PENDING: 1,
  APPROVED: 2,
  GENERATED: 3,
  REJECTED: 6,
};

function formatLicenseLabel(lic) {
  if (!lic) return "";
  const key = lic.LisansKey || `#${lic.Id}`;
  const paket = lic.PaketAdi || "";
  const bitis = lic.BitisTarih
    ? new Date(lic.BitisTarih).toLocaleDateString("tr-TR")
    : "—";
  const durum = lic.DurumLabel || (lic.Etkin ? "Aktif" : "Pasif");
  return [key, paket, bitis, durum].filter(Boolean).join(" · ");
}

function formatDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

export default function GastoraLicenseRequestForm() {
  const { id: talepId } = useParams();
  const isEditMode = Boolean(talepId);
  const { token, userData } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [loading, setLoading] = useState(isEditMode);
  const [customer, setCustomer] = useState({ customer: {} });
  const [gastoraLisanslar, setGastoraLisanslar] = useState([]);
  const [loadingLicenses, setLoadingLicenses] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusLocked, setStatusLocked] = useState(false);

  const [form, setForm] = useState({
    GastoraLisansId: "",
    LisansTipi: "Yıllık",
    Aciklama: "",
    Durum: STATUS_TYPES.PENDING,
    BaslangicTarih: "",
    BitisTarih: "",
  });

  const applyLicenseSelection = useCallback((lic) => {
    if (!lic?.Id) return;
    setSelectedLicense(lic);
    setForm((prev) => ({
      ...prev,
      GastoraLisansId: String(lic.Id),
      LisansTipi: lic.LisansTipi === "Demo" ? "Demo" : "Yıllık",
    }));
  }, []);

  const loadGastoraLicenses = useCallback(
    async (musteriId) => {
      if (!musteriId) {
        setGastoraLisanslar([]);
        return [];
      }
      setLoadingLicenses(true);
      try {
        const res = await axios.get(
          `${config.baseURL}/gastora-lisanslar/musteri-lisans/${musteriId}`,
          { headers },
        );
        const list = Array.isArray(res.data) ? res.data : [];
        setGastoraLisanslar(list);
        return list;
      } catch {
        setGastoraLisanslar([]);
        return [];
      } finally {
        setLoadingLicenses(false);
      }
    },
    [headers],
  );

  useEffect(() => {
    if (!isEditMode || !talepId) return;

    let cancelled = false;
    setLoading(true);

    axios
      .get(`${config.baseURL}/gastora-lisanslar/lisanstalep/${talepId}`, { headers })
      .then(async (res) => {
        if (cancelled) return;

        const talep = res.data?.data ?? res.data;
        if (!talep?.Id) {
          toast.error("Talep bulunamadı");
          navigate("/gastora/lisanslar", { replace: true });
          return;
        }

        const musteriId = talep.MusteriId;
        let customerData = talep.Musteri
          ? { ...talep.Musteri, Id: musteriId ?? talep.Musteri.Id }
          : null;

        if (musteriId && !customerData?.Unvan) {
          try {
            const mRes = await axios.get(`${config.baseURL}/musteriler/${musteriId}`, { headers });
            customerData = mRes.data;
          } catch {
            customerData = { Id: musteriId };
          }
        }

        setCustomer({
          customer: {
            ...(customerData || {}),
            Id: musteriId,
            BayiKodu: userData?.BayiKodu,
          },
        });

        const list = musteriId ? await loadGastoraLicenses(musteriId) : [];
        const lic =
          list.find((l) => String(l.Id) === String(talep.GastoraLisansId)) ||
          talep.GastoraLisans ||
          null;

        if (lic) {
          setSelectedLicense(lic);
        }

        const durum = Number(talep.Durum ?? STATUS_TYPES.PENDING);
        setStatusLocked(durum === STATUS_TYPES.GENERATED);

        setForm({
          GastoraLisansId: talep.GastoraLisansId ? String(talep.GastoraLisansId) : "",
          LisansTipi: talep.LisansTipi === "Demo" ? "Demo" : "Yıllık",
          Aciklama: talep.Aciklama || "",
          Durum: durum,
          BaslangicTarih: formatDateInput(talep.BaslangicTarih),
          BitisTarih: formatDateInput(talep.BitisTarih),
        });
      })
      .catch((e) => {
        if (cancelled) return;
        const msg =
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          "Talep yüklenemedi";
        toast.error(msg);
        navigate("/gastora/lisanslar", { replace: true });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isEditMode, talepId, headers, navigate, userData?.BayiKodu, loadGastoraLicenses]);

  const handleCustomerChange = useCallback(
    async (name, value) => {
      if (name !== "customer" || isEditMode) return;
      setCustomer({
        customer: {
          ...value,
          BayiKodu: userData?.BayiKodu,
        },
      });
      if (value?.Id) {
        const list = await loadGastoraLicenses(value.Id);
        setForm((prev) => {
          const current = list.find(
            (l) => String(l.Id) === String(prev.GastoraLisansId),
          );
          if (current) {
            setSelectedLicense(current);
            return prev;
          }
          if (list.length === 1) {
            applyLicenseSelection(list[0]);
            return {
              ...prev,
              GastoraLisansId: String(list[0].Id),
              LisansTipi: list[0].LisansTipi === "Demo" ? "Demo" : "Yıllık",
            };
          }
          setSelectedLicense(null);
          return { ...prev, GastoraLisansId: "" };
        });
      } else {
        setGastoraLisanslar([]);
        setSelectedLicense(null);
        setForm((prev) => ({ ...prev, GastoraLisansId: "" }));
      }
    },
    [loadGastoraLicenses, userData?.BayiKodu, applyLicenseSelection, isEditMode],
  );

  const handleCustomerLicensePick = (licenseId) => {
    if (statusLocked) return;
    const lic = gastoraLisanslar.find((l) => String(l.Id) === String(licenseId));
    if (lic) applyLicenseSelection(lic);
  };

  const handleSubmit = async () => {
    const musteriId = customer.customer?.Id;
    if (!form.GastoraLisansId) {
      toast.warning("Yenilenecek Gastro Lisansını seçin");
      return;
    }
    if (!musteriId) {
      toast.warning("Önce müşteri seçin");
      return;
    }

    const payload = {
      GastoraLisansId: parseInt(form.GastoraLisansId, 10),
      MusteriId: musteriId,
      KullaniciNumarasi: musteriId,
      LisansTipi: form.LisansTipi,
      Aciklama: form.Aciklama,
    };

    if (isEditMode) {
      payload.Durum = form.Durum;
      if (form.BaslangicTarih) {
        payload.BaslangicTarih = form.BaslangicTarih;
      }
    }

    try {
      setSaving(true);
      if (isEditMode) {
        await axios.put(
          `${config.baseURL}/gastora-lisanslar/lisanstalep/${talepId}`,
          payload,
          { headers },
        );
        toast.success("Gastro Lisans talebi güncellendi");
      } else {
        await axios.post(
          `${config.baseURL}/gastora-lisanslar/lisanstalep`,
          payload,
          { headers },
        );
        toast.success("Gastro Lisans yenileme talebi oluşturuldu");
      }
      navigate("/gastora/lisanslar");
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.response?.data?.hint ||
        (isEditMode ? "Talep güncellenemedi" : "Talep oluşturulamadı");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="cari-listesi-container">
      <Box sx={{ width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" }, mx: "auto", p: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          {isEditMode ? "Gastro Lisans Talebi Düzenle" : "Gastro Lisans Yenileme Talebi"}
        </Typography>

        {statusLocked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Onaylanmış ve lisansı güncellenmiş talepte düzenleme yapamazsınız.
          </Alert>
        )}

        <CustomerStep
          onChange={handleCustomerChange}
          initialData={customer}
          orderId={null}
          skipOrderFetch
          allowEditExistingCustomer={!isEditMode}
          isDisabled={isEditMode || statusLocked}
          isEditMode={isEditMode}
          customer={customer}
        />

        <Card elevation={0} sx={{ my: 2, border: "1px solid", borderColor: "divider" }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <VpnKeyIcon color="action" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700} sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}>
                Gastro Lisans Seçimi
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {customer.customer?.Id ? (
                <Grid item xs={12}>
                  {loadingLicenses ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Müşteri lisansları yükleniyor…
                      </Typography>
                    </Box>
                  ) : gastoraLisanslar.length > 0 || selectedLicense ? (
                    <FormControl fullWidth>
                      <InputLabel>Müşterinin Gastro Lisansları</InputLabel>
                      <Select
                        label="Müşterinin Gastro Lisansları"
                        value={form.GastoraLisansId ? String(form.GastoraLisansId) : ""}
                        onChange={(e) => handleCustomerLicensePick(e.target.value)}
                        disabled={statusLocked || isEditMode}
                      >
                        {(gastoraLisanslar.length > 0
                          ? gastoraLisanslar
                          : selectedLicense
                            ? [selectedLicense]
                            : []
                        ).map((lic) => (
                          <MenuItem key={lic.Id} value={String(lic.Id)}>
                            {formatLicenseLabel(lic)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      Seçili müşteriye ait Gastro Lisansı bulunamadı.
                    </Alert>
                  )}
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Önce müşteri seçin; ardından yenilenecek Gastro Lisansını listeden seçin.
                  </Alert>
                </Grid>
              )}

              {selectedLicense ? (
                <Grid item xs={12}>
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    Seçili: <strong>{selectedLicense.LisansKey}</strong>
                  </Alert>
                </Grid>
              ) : null}

              {isEditMode && form.BaslangicTarih ? (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Başlangıç Tarihi"
                      type="date"
                      value={form.BaslangicTarih}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, BaslangicTarih: e.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      disabled={statusLocked}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Bitiş Tarihi (talep)"
                      type="date"
                      value={form.BitisTarih}
                      InputLabelProps={{ shrink: true }}
                      disabled
                    />
                  </Grid>
                </>
              ) : null}
            </Grid>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Lisans Tipi</InputLabel>
              <Select
                label="Lisans Tipi"
                value={form.LisansTipi}
                onChange={(e) => setForm({ ...form, LisansTipi: e.target.value })}
                disabled={statusLocked}
              >
                <MenuItem value="Yıllık">Yıllık</MenuItem>
                <MenuItem value="Demo">Demo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Açıklama"
              value={form.Aciklama}
              onChange={(e) => setForm({ ...form, Aciklama: e.target.value })}
              disabled={statusLocked}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap", pb: 4 }}>
          <Button variant="outlined" onClick={() => navigate("/gastora/lisanslar")} disabled={saving}>
            İptal
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving || statusLocked}>
            {saving ? "Kaydediliyor…" : isEditMode ? "Güncelle" : "Kaydet"}
          </Button>
          {isEditMode && (
            <FormControl size="small" sx={{ minWidth: 180 }} disabled={statusLocked}>
              <InputLabel>Durum</InputLabel>
              <Select
                label="Durum"
                value={form.Durum}
                onChange={(e) => setForm((prev) => ({ ...prev, Durum: e.target.value }))}
              >
                <MenuItem value={STATUS_TYPES.PENDING}>Beklemede</MenuItem>
                <MenuItem value={STATUS_TYPES.APPROVED}>Onaylandı</MenuItem>
                {statusLocked && (
                  <MenuItem value={STATUS_TYPES.GENERATED}>Lisans Güncellendi</MenuItem>
                )}
                <MenuItem value={STATUS_TYPES.REJECTED}>Reddedildi</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>
    </div>
  );
}
